// src/components/StockCard.jsx
//
// One unit in the grid. The whole card is the link, so the tap target on a
// phone is the card rather than a small "view" button inside it.

import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Package } from "lucide-react";
import { GlowCard } from "./Effects";
import { makeModel, mediaUrl, priceLabel } from "../lib/format";
import { isNew } from "../lib/search";

export default function StockCard({ item }) {
  const img = item.images?.[0];
  const second = item.images?.[1];
  const mm = makeModel(item);
  const fresh = isNew(item);

  return (
    <GlowCard className="h-full rounded-2xl border border-line bg-surface transition-colors hover:border-brooke-red/50">
      <Link to={`/${item.slug}`} className="group flex h-full flex-col">
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-2xl bg-surface2">
          {img ? (
            <img
              src={mediaUrl(img.thumb || img.path)}
              alt={item.title}
              loading="lazy"
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="grid h-full place-items-center text-subtle">
              <Package size={30} strokeWidth={1.4} />
            </div>
          )}

          {/* Second photo fades in on hover, so a desktop visitor sees
              another angle without opening the listing. Lazy, so it costs
              nothing until the grid is on screen. */}
          {second && (
            <img
              src={mediaUrl(second.thumb || second.path)}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-0 transition duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
            />
          )}

          <div className="absolute left-3 top-3 flex gap-1.5">
            {fresh && (
              <span className="rounded-full bg-brooke-red px-2.5 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.16em] text-white">
                New
              </span>
            )}
            {item.category && (
              <span className="rounded-full border border-line bg-bg/85 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.16em] text-muted backdrop-blur">
                {item.category}
              </span>
            )}
          </div>

          {item.images?.length > 1 && (
            <span className="absolute bottom-3 right-3 rounded-full bg-bg/85 px-2 py-0.5 font-mono text-[9.5px] text-subtle backdrop-blur">
              {item.images.length} photos
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col p-5">
          <h3 className="font-arch text-lg font-extrabold leading-snug tracking-tight text-ink">
            {item.title}
          </h3>
          {mm && (
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.14em] text-subtle">
              {mm}
            </p>
          )}
          {item.condition_note && (
            <p className="mt-3 line-clamp-2 text-[13.5px] leading-relaxed text-muted">
              {item.condition_note}
            </p>
          )}

          <div className="mt-auto flex items-end justify-between gap-3 pt-5">
            <span className="font-mono text-[15px] font-bold tabular-nums text-led">
              {priceLabel(item)}
            </span>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line text-subtle transition group-hover:border-brooke-red group-hover:text-brooke-red">
              <ArrowRight size={15} />
            </span>
          </div>
        </div>
      </Link>
    </GlowCard>
  );
}

export function StockCardSkeleton() {
  return (
    <div className="rounded-2xl border border-line bg-surface">
      <div className="aspect-[4/3] animate-pulse rounded-t-2xl bg-surface2" />
      <div className="space-y-3 p-5">
        <div className="h-5 w-3/4 animate-pulse rounded bg-surface2" />
        <div className="h-3 w-1/3 animate-pulse rounded bg-surface2" />
        <div className="h-3 w-full animate-pulse rounded bg-surface2" />
        <div className="h-4 w-1/4 animate-pulse rounded bg-surface2" />
      </div>
    </div>
  );
}